/**
 * Bottom-left live-session notice toasts (chat / Q&A / announcement / waiting).
 * Shows up to the last 3 messages; each auto-dismisses after 3s (× to close).
 * Optional `action` makes the toast clickable → opens the related panel.
 */

import React, { useEffect } from "react";

export type LiveSessionNoticeAction =
  | { type: "open_qa"; qaId?: string }
  | { type: "open_chat"; messageId?: string; peerId?: string }
  | { type: "open_announcements"; announcementId?: string }
  | { type: "open_polls"; pollId?: string }
  | { type: "open_participants" };

export type LiveSessionNotice = {
  id: string;
  text: string;
  action?: LiveSessionNoticeAction;
  /** Override default auto-dismiss (ms). */
  durationMs?: number;
};

const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 3000;

type Props = {
  /** Preferred: stack of notices (newest last). */
  notices?: LiveSessionNotice[];
  /** @deprecated Prefer `notices` — single notice still supported. */
  notice?: LiveSessionNotice | null;
  onDismiss: (id?: string) => void;
  /** Called when a notice with an action is clicked (before dismiss). */
  onAction?: (notice: LiveSessionNotice) => void;
  durationMs?: number;
  /** Distance from bottom edge for the stack. */
  bottom?: number;
};

function NoticeBubble({
  notice,
  onDismiss,
  onAction,
  durationMs,
}: {
  notice: LiveSessionNotice;
  onDismiss: (id: string) => void;
  onAction?: (notice: LiveSessionNotice) => void;
  durationMs: number;
}) {
  const ttl = notice.durationMs ?? durationMs;

  useEffect(() => {
    const t = window.setTimeout(() => onDismiss(notice.id), ttl);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice.id, ttl]);

  const clickable = Boolean(notice.action && onAction);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "relative",
        marginTop: 12,
        marginLeft: 12,
        maxWidth: "min(380px, calc(100vw - 40px))",
        pointerEvents: "auto",
      }}
    >
      <button
        type="button"
        onClick={() => onDismiss(notice.id)}
        aria-label="Dismiss"
        style={{
          position: "absolute",
          top: -8,
          left: -8,
          width: 24,
          height: 24,
          borderRadius: "50%",
          border: "1.5px solid #fff",
          background: "#000",
          color: "#fff",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          zIndex: 1,
        }}
      >
        ×
      </button>
      <div
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={() => {
          if (!clickable) return;
          onAction?.(notice);
          onDismiss(notice.id);
        }}
        onKeyDown={(e) => {
          if (!clickable) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onAction?.(notice);
            onDismiss(notice.id);
          }
        }}
        style={{
          background: "#000",
          color: "#fff",
          borderRadius: 18,
          padding: "14px 18px",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1.35,
          boxShadow: "0 10px 28px rgba(0,0,0,0.4)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          cursor: clickable ? "pointer" : "default",
        }}
      >
        {notice.text}
        {clickable ? (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              fontWeight: 600,
              color: "#93c5fd",
            }}
          >
            Click to view
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function LiveSessionNoticeToast({
  notices,
  notice = null,
  onDismiss,
  onAction,
  durationMs = DEFAULT_DURATION_MS,
  bottom = 20,
}: Props) {
  const list = (notices && notices.length > 0
    ? notices
    : notice
      ? [notice]
      : []
  ).slice(-MAX_VISIBLE);

  if (list.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 20,
        bottom,
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 10,
        pointerEvents: "none",
      }}
    >
      {list.map((n) => (
        <NoticeBubble
          key={n.id}
          notice={n}
          durationMs={durationMs}
          onDismiss={(id) => onDismiss(id)}
          onAction={onAction}
        />
      ))}
    </div>
  );
}

export default LiveSessionNoticeToast;
